import { Action, Reducer } from 'redux';
import { AppServices } from '../../../AppServices';
import '../../../utilities/Utilities';
import { ResultType } from '../../Models/Api/ApiResponse';
import { SurveyEditorCreateNewSurveyRequestData } from '../../Models/Api/SurveyEditorCreateNewSurveyRequestModel';
import { InviteSupplierModel } from '../../Models/Supplier/Supplier';
import { ColumnRequiredFlags, SurveyDataRequested, DisplaySection, emptySurveyMetadata, emptySurveyRequest, OfflineResponseApiData, QuestionnairePageType, SurveyableColumn, SurveyData, SurveyMode, SurveyRequest, SurveyRequestedBomColumn, SurveyRequestApiRequestBundle, SurveySendStateType, SurveyState, SurveyTemplateDataModel, SurveyUi, SurveyRequestInventoryMetadata, SaveTemplateMode } from '../../Models/Surveys/SurveyDataModel';
import { ApplicationState, AppThunkAction, AppThunkActionAsync } from './../../';
import { DataResult, ToxnotServiceClient } from './../../ToxnotServiceClient';
import RevisionRequestManager from './RevisionRequestManager';
import * as Models from '../../../store/Models/Surveys/SurveyDataModel';
import { InventoryIdentifiers } from './SurveyResponse/SurveyResponseModels';

// ==============================================================================

//
// these are the default "required" flags for selected bom columns
// anything not in this list will default to { requiredInSummaryRow: false, requiredInNonSummaryNonLeafRows: true, requiredInLeafRows: true }
//
export const columnIdToDefaultColumnRequiredFlagsTable: Readonly<{ [columnId: string]: ColumnRequiredFlags }> = {
    "level": { requiredInSummaryRow: true, requiredInNonSummaryNonLeafRows: true, requiredInLeafRows: true },
    "part-name": { requiredInSummaryRow: false, requiredInNonSummaryNonLeafRows: true, requiredInLeafRows: false },
    "casrn": { requiredInSummaryRow: false, requiredInNonSummaryNonLeafRows: false, requiredInLeafRows: true },
    "role": { requiredInSummaryRow: false, requiredInNonSummaryNonLeafRows: false, requiredInLeafRows: true },
    "percent-post-consumer-recycled-content": { requiredInSummaryRow: true, requiredInNonSummaryNonLeafRows: false, requiredInLeafRows: false },
    "percent-pre-consumer-recycled-content": { requiredInSummaryRow: true, requiredInNonSummaryNonLeafRows: false, requiredInLeafRows: false },
    "recycled-content": { requiredInSummaryRow: true, requiredInNonSummaryNonLeafRows: false, requiredInLeafRows: false },
    "material-type": { requiredInSummaryRow: true, requiredInNonSummaryNonLeafRows: false, requiredInLeafRows: false },
    "component-percent-by-weight": { requiredInSummaryRow: false, requiredInNonSummaryNonLeafRows: false, requiredInLeafRows: true },
    "percent-by-weight": { requiredInSummaryRow: false, requiredInNonSummaryNonLeafRows: true, requiredInLeafRows: true },
    "weight": { requiredInSummaryRow: false, requiredInNonSummaryNonLeafRows: true, requiredInLeafRows: true },
    "unit-weight": { requiredInSummaryRow: true, requiredInNonSummaryNonLeafRows: true, requiredInLeafRows: false },
    "quantity": { requiredInSummaryRow: false, requiredInNonSummaryNonLeafRows: true, requiredInLeafRows: true }
};

//
// these columns can't be set to "required" in the summary row in a survey because they can't be edited in the summary row
//
const disabledColumnsInSummaryRow: Readonly<{ [columnId: string]: boolean }> = {
    "alternative": true,
    "component-percent-by-weight": true,
    "percent-by-weight": true,
    "percent-of-wet-ingredient-in-final-product": true,
    "quantity": true,
    "role": true,
    "version-description": true,
    "weight": true,
    "wet-component-percent-by-weight": true,
    "wet-overall-percent-by-weight": true,
    "wet-weight": true,
};

export function isColumnDisabledInSummaryRow(columnId: string) {
    return disabledColumnsInSummaryRow[columnId] ?? false;
}

export const standardSurveyableColumns: Readonly<SurveyableColumn[]> = [
    { columnId: "level", canRemove: false, pairedColumnId: null, isAdvancedColumn: false },
    { columnId: "part-name", canRemove: false, pairedColumnId: null, isAdvancedColumn: false },
    { columnId: "casrn", canRemove: false, pairedColumnId: null, isAdvancedColumn: false },
    { columnId: "percent-by-weight", canRemove: false, pairedColumnId: null, isAdvancedColumn: false },
    { columnId: "component-percent-by-weight", canRemove: true, pairedColumnId: null, isAdvancedColumn: false },
    { columnId: "percent-pre-consumer-recycled-content", pairedColumnId: "percent-post-consumer-recycled-content", canRemove: true, isAdvancedColumn: false },
    { columnId: "role", canRemove: true, pairedColumnId: null, isAdvancedColumn: false },
    { columnId: "description", canRemove: true, pairedColumnId: null, isAdvancedColumn: false },
    { columnId: "notes", canRemove: true, pairedColumnId: null, isAdvancedColumn: false },
    { columnId: "c2c-percent-rapidly-renewable-content", canRemove: true, pairedColumnId: null, isAdvancedColumn: false },
    { columnId: "c2c-description-of-rapidly-renewable-content", canRemove: true, pairedColumnId: null, isAdvancedColumn: false },
    { columnId: "c2c-recyclable", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "c2c-biodegradable", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "c2c-disassemblability", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "wet-component-percent-by-weight", pairedColumnId: "percent-of-wet-ingredient-in-final-product", canRemove: true, isAdvancedColumn: true },
    { columnId: "residuals-and-impurities-notes", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "material-type", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "nanomaterial", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "supplier", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "unit-weight", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "threshold", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "quantity", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "scrap-rate", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "supplier-part-number", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "scip-concern-element-mixture-category", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "scip-concern-element-material-category", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "unit-of-measure", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "recycled-content", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "data-source", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "hpd-special-condition", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "reach-number", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "product-number", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "pbt-or-vpvb", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "clp-classification", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "physical-state", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "tsca-accession", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "pmn", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "oecd-code", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "authorisation-granted", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "confidential-business-information", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
    { columnId: "total-organic-fluorine-content", canRemove: true, pairedColumnId: null, isAdvancedColumn: true },
];

export const additionalIdentityColumnsIds: { [columnId: string]: (inventory: InventoryIdentifiers) => string | number } = {
    ["part-name"]: (inventory) => inventory.name,
    ["description"]: (inventory) => inventory.description,
    ["product-number"]: (inventory) => inventory.productNumber,
    ["supplier-part-number"]: (inventory) => inventory.supplierPartNumber
};

// ===============================================================================

/**
 * Builds the Material Disclosure picker list from backend metadata.
 * Standard columns preserve their static paired/advanced/canRemove behavior from
 * `standardSurveyableColumns`; normal custom columns (`extensible-column-N`) come
 * from the backend and carry their stable `columnName` snapshot.
 * Public/private `ExtensibleFlag` columns are excluded in v1.
 * Falls back to the static standard list when metadata is not yet loaded.
 */
export function getSurveyableColumnsForPicker(metadataColumns: Readonly<SurveyableColumn[]>): SurveyableColumn[] {
    const standardById: { [columnId: string]: SurveyableColumn } = {};
    const metadataById: { [columnId: string]: SurveyableColumn } = {};

    for (const column of standardSurveyableColumns) {
        standardById[column.columnId] = column;
    }

    for (const column of metadataColumns ?? []) {
        metadataById[column.columnId] = column;
    }

    const standardColumns = standardSurveyableColumns.map(column => {
        const metadataColumn = metadataById[column.columnId];

        return {
            ...column,
            columnName: metadataColumn?.columnName,
            isCustomColumn: false,
            isFlagColumn: metadataColumn?.isFlagColumn,
            isEnabled: metadataColumn?.isEnabled
        };
    });

    const customColumns = (metadataColumns ?? [])
        .filter(column => column.isCustomColumn && !column.isFlagColumn && column.columnName)
        .filter(column => !standardById[column.columnId])
        .map(column => ({
            columnId: column.columnId,
            columnName: column.columnName,
            pairedColumnId: null,
            canRemove: true,
            isAdvancedColumn: false,
            isCustomColumn: true,
            isFlagColumn: false,
            isEnabled: column.isEnabled
        }));

    return standardColumns.concat(customColumns);
}

/**
 * Preserves existing BOM column snapshots (including `columnName`) when a payload
 * is built. Never re-enriches an existing custom field's `columnName` from the current
 * positional friendly-name map because that map can reference a different column after
 * custom-column reordering.
 */
export function preserveBomColumnNames(
    fields: Readonly<SurveyRequestedBomColumn[]>
): SurveyRequestedBomColumn[] {
    return fields.map(field => ({
        ...field
    }));
}

/**
 * Normalizes template BOM columns on apply so drifted positional custom column ids
 * are remapped to the current positional id for the same case-insensitive `columnName`.
 * Standard fields are matched by stable `columnId`. Custom fields with a deleted,
 * renamed, or type-changed target are dropped.
 */
export function normalizeTemplateBomColumns(
    templateColumns: Readonly<SurveyRequestedBomColumn[]>,
    standardColumns: Readonly<SurveyableColumn[]>,
    metadataColumns: Readonly<SurveyableColumn[]>
): SurveyRequestedBomColumn[] {
    const standardById: { [columnId: string]: SurveyableColumn } = {};
    const customByName: { [lowerName: string]: SurveyableColumn } = {};

    for (const column of standardColumns) {
        standardById[column.columnId] = column;
    }

    for (const column of metadataColumns ?? []) {
        if (column.isFlagColumn) {
            continue;
        }

        if (column.isCustomColumn && column.columnName) {
            customByName[column.columnName.trim().toLowerCase()] = column;
        }
    }

    const normalized: SurveyRequestedBomColumn[] = [];

    for (const field of templateColumns) {
        if (!field.columnId.startsWith("extensible-column-")) {
            if (standardById[field.columnId]) {
                normalized.push({ ...field });
            }
            continue;
        }

        const nameKey = field.columnName?.trim().toLowerCase();
        if (!nameKey) {
            // Never infer a custom column identity from the current positional id.
            continue;
        }

        const remappedColumn = customByName[nameKey];
        if (!remappedColumn) {
            continue;
        }

        normalized.push({
            ...field,
            columnId: remappedColumn.columnId,
            columnName: remappedColumn.columnName ?? field.columnName
        });
    }

    return normalized;
}

// ===============================================================================

export const SurveySetUIState = "SET_STATE_UI";
export interface SurveySetUIState {
    type: typeof SurveySetUIState;
    value: Partial<SurveyUi>;
}

export const SurveySetSurveyRequest = "SET_SURVEY_REQUEST";
export interface SurveySetSurveyRequest {
    type: typeof SurveySetSurveyRequest;
    value: Partial<SurveyRequest>;
}

export const SurveySetSurveyMetadata = "SET_SURVEY_METADATA";
export interface SurveySetSurveyMetadata {
    type: typeof SurveySetSurveyMetadata;
    value: Partial<SurveyData>;
}

export function getInitialState(usersLanguage: string, surveyIsDraft: boolean): SurveyState {
    return {
        surveyUi: {
            currentPage: surveyIsDraft ? 0 : 4,
            currentLanguage: usersLanguage,
            currentState: SurveyMode.New,
            currentSection: DisplaySection.MaterialDisclosure,
            showCustomName: false,
            showErrorMessage: false,
            dataLoaded: false,
            showDataSaveMessage: false,
            sentReminderAlert: false,
            errorReminderAlert: false,
            errorReminderNoMessageAlert: false,
            sendReminderMessage: "",
            showErrorScreen: false,
            showNotFoundErrorScreen: false,
            surveySendStateType: SurveySendStateType.None,
            responseHistoryListIsExpanded: false,
            revisionRequestManager: null,
            offlineResponseSupplierResponseInputText: "",
            offlineResponseNotesInputText: "",
            offlineResponseAttachments: null,
            addOfflineResponseSuccess: null,
            sendGranularRevisionRequestMode: false,
            viewGranularRevisionRequestHistoryEntryId: null,
            surveyHasRequestsThatAreRedundantWithCustomQuestionnaire: false,
            showAdvancedIngredientColumns: false,
            activeSendOfflineProcessId: "",
            activeAcceptOfflineProcessId: "",
            showMaterialsTables: true,
            showMissingSupplierMessage: false,
            currentTemplateId: null,
            saveTemplateMode: SaveTemplateMode.Default,
            saveTemplateAnyRadioSelected: false,
            newTemplateName: "",
            saveTemplateSelectedTemplateId: null
        },
        surveyRequest: emptySurveyRequest(usersLanguage, columnIdToDefaultColumnRequiredFlagsTable),
        surveyMetadata: emptySurveyMetadata()
    }
}

// ===============================================================================

type KnownAction = SurveySetSurveyMetadata | SurveySetUIState | SurveySetSurveyRequest;

export const actionCreators = {
    // ===============================================================================

    showSaveTemplateModal: (mode: SaveTemplateMode): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const currentTemplateId = getState().createAndEditSurveysState?.surveyUi.currentTemplateId;

        dispatch({
            type: SurveySetUIState,
            value: {
                saveTemplateMode: mode,
                saveTemplateAnyRadioSelected: false,
                newTemplateName: "",
                saveTemplateSelectedTemplateId: currentTemplateId
            }
        });

        $("#survey-template-save-modal").modal('show');
    },

    // ===============================================================================

    addReminder: (date: string, message: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = getState().createAndEditSurveysState;
        if (!state) {
            return;
        }

        let scheduledDaysSinceRequestSince: number = 0;
        let scheduledDate: Date = new Date();

        if (state.surveyUi.currentState === SurveyMode.Template) {
            const now = new Date();
            scheduledDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
            scheduledDate.setDate(scheduledDate.getDate() + Number.parseInt(date));
            scheduledDaysSinceRequestSince = Number.parseInt(date);

        } else {
            const splits = date.split('-').map(s => parseInt(s));
            const scheduledSendDate = new Date(splits[0], splits[1] - 1, splits[2]);
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            scheduledDate = scheduledSendDate;
            scheduledDaysSinceRequestSince = Math.ceil((scheduledSendDate.getTime() - today.getTime()) / 1000.0 / 60.0 / 60.0 / 24.0) - 1
        }

        const value = {
            ...state.surveyRequest,
            scheduledReminders: [
                ...state.surveyRequest.scheduledReminders,
                {
                    ownerId: null,
                    requestId: null,
                    reminderId: null,
                    languageToReminderMessageMap: { [state.surveyUi.currentLanguage]: message },
                    scheduledDaysSinceRequestSent: scheduledDaysSinceRequestSince,
                    scheduledSendDate: scheduledDate.getTime(),
                    state: "NeedsToBeSent",
                    peekLockTimeout: 0,
                    scheduledSendDateUnix: scheduledDate.getTime(),
                }
            ]
        }

        dispatch({
            type: SurveySetSurveyRequest,
            value: value
        });

        saveData(dispatch, getState);
    },

    // ===============================================================================

    removeReminder: (index: number): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = getState().createAndEditSurveysState;
        if (!state) {
            return;
        }

        const value = {
            ...state.surveyRequest,
            scheduledReminders: [
                ...state.surveyRequest.scheduledReminders.filter((value, i) => i != index)
            ]
        }

        dispatch({
            type: SurveySetSurveyRequest,
            value: value
        });

        saveData(dispatch, getState);
    },

    //================================================================================

    editReminderMessage: (index: number, newMessage: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = getState().createAndEditSurveysState;
        if (!state) {
            return;
        }

        const value = {
            ...state.surveyRequest,
            scheduledReminders: [
                ...state.surveyRequest.scheduledReminders.map((value, i) => {
                    if (i === index) {
                        value.languageToReminderMessageMap[state.surveyUi.currentLanguage] = newMessage
                    }
                    return value
                })
            ]
        }

        dispatch({
            type: SurveySetSurveyRequest,
            value: value
        });

        saveData(dispatch, getState);
    },

    //================================================================================

    editNote: (index: number, newMessage: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = getState().createAndEditSurveysState;
        if (!state) {
            return;
        }

        const value = {
            ...state.surveyRequest,
            notes: [
                ...state.surveyRequest.notes.map((value, i) => {
                    if (i === index) {
                        value.note = newMessage
                    }
                    return value
                })
            ]
        }

        dispatch({
            type: SurveySetSurveyRequest,
            value: value
        });

        saveData(dispatch, getState);
    },

    // ===============================================================================

    removeOtherDocuments: (index: number): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = getState().createAndEditSurveysState;
        if (!state) {
            return;
        }


        const value = {
            ...state.surveyRequest,
            dataRequested: {
                ...state.surveyRequest.dataRequested,
                documentsCertificationList: [
                    ...state.surveyRequest.dataRequested.documentsCertificationList.filter((value, i) => i != index)
                ]
            }
        }

        dispatch({
            type: SurveySetSurveyRequest,
            value: value
        });

        saveData(dispatch, getState);
    },

    //================================================================================

    editOtherDocuments: (index: number, newMessage: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = getState().createAndEditSurveysState;
        if (!state) {
            return;
        }

        const value = {
            ...state.surveyRequest,
            dataRequested: {
                ...state.surveyRequest.dataRequested,
                documentsCertificationList: [
                    ...state.surveyRequest.dataRequested.documentsCertificationList.map((value, i) => {
                        if (i === index) {
                            value.documentNameIfOther = newMessage
                        }
                        return value
                    })
                ]
            }
        }

        dispatch({
            type: SurveySetSurveyRequest,
            value: value
        });

        saveData(dispatch, getState);

    },

    // ===============================================================================

    setLanguageMap: (item: string, key: string, value: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = getState().createAndEditSurveysState;
        if (!state) {
            return;
        }

        const getValue = (item: string) => {
            switch (item) {
                case "languageToEmailSubjectMap":
                    return {
                        ...state.surveyRequest,
                        languageToEmailSubjectMap: {
                            ...state.surveyRequest.languageToEmailSubjectMap,
                            [key]: value
                        }
                    }
                case "languageToEmailMessageMap":
                    return {
                        ...state.surveyRequest,
                        languageToEmailMessageMap: {
                            ...state.surveyRequest.languageToEmailMessageMap,
                            [key]: value
                        }
                    }
                case "languageToRequestInstructionsMap":
                    return {
                        ...state.surveyRequest,
                        languageToRequestInstructionsMap: {
                            ...state.surveyRequest.languageToEmailSubjectMap,
                            [key]: value
                        }
                    }
                case "languageToCompletionInstructionsMap":
                    return {
                        ...state.surveyRequest,
                        languageToCompletionInstructionsMap: {
                            ...state.surveyRequest.languageToCompletionInstructionsMap,
                            [key]: value
                        }
                    }
                default:
                    return state.surveyRequest
            }
        }
        dispatch({
            type: SurveySetSurveyRequest,
            value: getValue(item)
        });
    },

    // ===============================================================================

    goToMaterialsTab: () => ({
        type: SurveySetUIState,
        value: {
            currentPage: 0
        }
    }),

    // ===============================================================================

    sendSurvey: (): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const applicationState = getState();

        applicationState.appServices?.trackEvent("Begin_sendSurvey");

        const appServices = applicationState.appServices;

        const surveyEditorstate = applicationState.createAndEditSurveysState;

        const selectMaterialsSuppliersTableState = applicationState.selectSuppliersMaterialsTableState;

        if (!surveyEditorstate || !appServices || !selectMaterialsSuppliersTableState) {
            return;
        }

        const isObtainmentAccount = appServices.userContext?.userCapabilities.obtainmentServicesEnabled ?? false;
        const selectedTemplateId = surveyEditorstate.surveyUi.currentTemplateId ?? surveyEditorstate.surveyRequest.surveyTemplateId;
        const hasSelectedCampaign = !!surveyEditorstate.surveyRequest.campaignId?.trim();

        if (isObtainmentAccount && selectedTemplateId === null) {
            alert(appServices.localizeString("Please select a survey template before sending obtainment surveys."));
            dispatch({
                type: SurveySetUIState,
                value: {
                    currentPage: 0
                }
            });
            return;
        }

        if (isObtainmentAccount && !hasSelectedCampaign) {
            alert(appServices.localizeString("Please select a campaign before sending obtainment surveys."));
            dispatch({
                type: SurveySetUIState,
                value: {
                    currentPage: 0
                }
            });
            return;
        }

        const hasEmail = surveyEditorstate.surveyRequest.fromEmailAddress !== "" && surveyEditorstate.surveyRequest.fromEmailAddress !== null;
        const hasName = surveyEditorstate.surveyRequest.fromName !== "" && surveyEditorstate.surveyRequest.fromName !== null;
        const hasDataRequested = surveyEditorstate.surveyRequest.dataRequested.materialDisclosureAsked ||
            surveyEditorstate.surveyRequest.dataRequested.circularityDataAsked ||
            surveyEditorstate.surveyRequest.dataRequested.complianceDataAsked ||
            surveyEditorstate.surveyRequest.dataRequested.customQuestionsAsked ||
            surveyEditorstate.surveyRequest.dataRequested.documentsAsked ||
            surveyEditorstate.surveyRequest.dataRequested.sustainabilityDataAsked ||
            surveyEditorstate.surveyRequest.dataRequested.embodiedCarbonDataAsked ||
            surveyEditorstate.surveyRequest.dataRequested.waterUseDataAsked ||
            surveyEditorstate.surveyRequest.dataRequested.carbonTransparencyDataAsked ||
            surveyEditorstate.surveyRequest.dataRequested.carbonOtherDataAsked ||
            surveyEditorstate.surveyRequest.dataRequested.vocDataAsked ||
            surveyEditorstate.surveyRequest.dataRequested.vocTransparencyDataAsked ||
            surveyEditorstate.surveyRequest.dataRequested.vocOtherDataAsked;

        if (!(hasEmail && hasName && hasDataRequested)) {
            alert(appServices.localizeString("You must fill in your requestor email, and request some data."));
            if (!hasDataRequested) {
                dispatch({
                    type: SurveySetUIState,
                    value: {
                        currentPage: 1
                    }
                });
            }
            else if (!hasName || !hasEmail) {
                dispatch({
                    type: SurveySetUIState,
                    value: {
                        currentPage: 3
                    }
                });
            }
            return;
        }

        applicationState.appServices?.trackEvent(
            "sendSurvey-withContext:",
            {
                fromName: surveyEditorstate.surveyRequest.fromName,
                fromEmailAddress: surveyEditorstate.surveyRequest.fromEmailAddress
            });

        if (surveyEditorstate.surveyRequest.dataRequested.complianceList.includes("SCIP") && !surveyEditorstate.surveyRequest.dataRequested.complianceList.includes("Reach Candidate")) {
            alert(appServices.localizeString("You must include REACH Candidate to include SCIP as well."));
            dispatch({
                type: SurveySetUIState,
                value: {
                    currentPage: 1,
                    currentSection: DisplaySection.ComplianceData
                }
            });
            return;
        }

        $("#survey-update-confirmation-modal").modal("hide");

        dispatch({
            type: SurveySetUIState,
            value: {
                surveySendStateType: SurveySendStateType.SendingSurvey
            }
        });

        $("#offline-survey-progress-modal").modal("show");

        let apiResult: DataResult<string>;

        if (surveyEditorstate.surveyRequest.requestId != null) {

            apiResult = await sendDraftSurvey({ surveyData: surveyEditorstate.surveyMetadata, surveyRequest: surveyEditorstate.surveyRequest, shouldSend: true }, surveyEditorstate.surveyRequest.requestId, appServices, dispatch);

        } else {

            apiResult = await sendOrSaveNewSurvey(surveyEditorstate.surveyMetadata, surveyEditorstate.surveyRequest, surveyEditorstate.surveyUi, selectMaterialsSuppliersTableState?.supplierRowModels, appServices, dispatch, true);

        }

        if (apiResult.result !== ResultType.Ok) {
            if (apiResult.result === "InventoryContainsLinkedMaterials") {
                dispatch({
                    type: SurveySetUIState,
                    value: {
                        surveySendStateType: SurveySendStateType.SendSurveyContainsLinkedMaterials
                    }
                });
            }
            else if (apiResult.result === "BadCcEmailList") {
                dispatch({
                    type: SurveySetUIState,
                    value: {
                        surveySendStateType: SurveySendStateType.SendSurveyContainsInvalidCcEmailList
                    }
                });
            }
            else if (apiResult.result === "BadEmailAddress") {
                dispatch({
                    type: SurveySetUIState,
                    value: {
                        surveySendStateType: SurveySendStateType.SendSurveyContainsInvalidFromEmaillAddress
                    }
                });
            }
            else if (apiResult.result == "InvalidEmailToken") {
                dispatch({
                    type: SurveySetUIState,
                    value: {
                        surveySendStateType: SurveySendStateType.SendSurveyRefreshTokenFailed
                    }
                });
            }
            else {
                dispatch({
                    type: SurveySetUIState,
                    value: {
                        surveySendStateType: SurveySendStateType.SendSurveyFailed
                    }
                });
            }
        }

        if (apiResult.data == null) {
            dispatch({
                type: SurveySetUIState,
                value: {
                    surveySendStateType: SurveySendStateType.SendSurveyFailed
                }
            });
        }

        if (apiResult.success && apiResult.data !== null) {
            dispatch({
                type: SurveySetUIState,
                value: {
                    surveySendStateType: SurveySendStateType.SendSurveySucceeded,
                    activeSendOfflineProcessId: apiResult.data,
                    showDataSaveMessage: false
                }
            });
        }

    },

    // ===============================================================================

    getExistingSurveyState: (requestId: string): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        await getSurvey(requestId, dispatch);
    },

    // ===============================================================================

    refreshRevisionRequestData: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const applicationState = getState();

        if (!applicationState.createAndEditSurveysState || !applicationState.createAndEditSurveysState.surveyUi.revisionRequestManager) {
            return;
        }

        let refreshedRevisionRequestManager = RevisionRequestManager.fromOther(applicationState.createAndEditSurveysState.surveyUi.revisionRequestManager);

        refreshedRevisionRequestManager.loadSurveyHistoryEntryRevisionRequests();

        dispatch({
            type: SurveySetUIState,
            value: {
                revisionRequestManager: refreshedRevisionRequestManager
            }
        });
    },
    // ===============================================================================

    setSurveyAsNoValidationRequired: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = getState().createAndEditSurveysState;
        if (!state) {
            return;
        }
        surveySetAsNoValidationRequired(state.surveyRequest.requestId, dispatch);
    },

    // ===============================================================================

    resetSendReminderState: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();
        if (!appState || !appState.createAndEditSurveysState) {
            return;
        }
        dispatch({
            type: SurveySetUIState,
            value: {
                sendReminderMessage: "",
                errorReminderNoMessageAlert: false,
                errorReminderAlert: false,
                sentReminderAlert: false
            }
        });
    },

    // ===============================================================================

    sendReminder: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();
        if (!appState || !appState.createAndEditSurveysState) {
            return;
        }
        if (appState.createAndEditSurveysState.surveyUi.sendReminderMessage === "") {
            dispatch({
                type: SurveySetUIState,
                value: {
                    errorReminderNoMessageAlert: true,
                    errorReminderAlert: false,
                    sentReminderAlert: false
                }
            });
            return;
        }
        sendReminder(appState.createAndEditSurveysState.surveyRequest.requestId, appState.createAndEditSurveysState.surveyUi.sendReminderMessage, dispatch);
    },

    // ===============================================================================

    deleteTemplate: (templateId: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();
        if (!appState || !appState.createAndEditSurveysState) {
            return;
        }
        deleteSurveyTemplate(templateId, dispatch, appState.createAndEditSurveysState.surveyMetadata.templates);
    },

    // ===============================================================================

    saveTemplate: (existingTemplateId: string | null): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const appState = getState();
        if (!appState || !appState.createAndEditSurveysState) {
            return;
        }

        await saveSurveyTemplate(dispatch, appState.createAndEditSurveysState, existingTemplateId, appState.appServices!);
        // TODO: progress indicator?

        $("#survey-template-save-modal").modal("hide");

        // Refresh the templates data to get updated last modified info
        const currentState = getState();
        if (currentState && currentState.createAndEditSurveysState) {
            // Get inventory IDs from the current survey request (simplified for templates)
            const inventoryIds: string[] = [];
            await actionCreators.getNewSurveyMetadata(inventoryIds, true)(dispatch, getState);
            
            // After refreshing data, trigger a UI state update to ensure the default button state is recalculated
            const newState = getState();
            if (newState && newState.createAndEditSurveysState && newState.createAndEditSurveysState.surveyMetadata) {
                const currentTemplateId = newState.createAndEditSurveysState.surveyUi.currentTemplateId || existingTemplateId;
                const defaultTemplateId = newState.createAndEditSurveysState.surveyMetadata.defaultSurveyTemplateId;
                const isDefault = currentTemplateId === defaultTemplateId;
                
                dispatch({
                    type: SurveySetUIState,
                    value: {
                        // This will trigger componentDidUpdate in the component to update the default button state
                        currentTemplateId: currentTemplateId
                    }
                });
            }
        }
    },

    setAsDefaultTemplate: (templateId: string): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const serviceClient = new ToxnotServiceClient();
        const result = await serviceClient.setSurveyTemplateAsDefault(templateId);
        
        if (!result.success) {
            dispatch({
                type: SurveySetUIState,
                value: {
                    showErrorMessage: true
                }
            });
            return;
        }

        // Update the default template ID in the current state
        const state = getState();
        if (state && state.createAndEditSurveysState && state.createAndEditSurveysState.surveyMetadata) {
            dispatch({
                type: SurveySetSurveyMetadata,
                value: {
                    ...state.createAndEditSurveysState.surveyMetadata,
                    defaultSurveyTemplateId: templateId
                }
            });
        }
    },

    // ===============================================================================

    getNewSurveyMetadata: (inventoryIdsForSurvey: string[], forTemplates: boolean = false): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        if (!state || !state.createAndEditSurveysState) {
            return;
        }

        const serviceClient = new ToxnotServiceClient();

        const metadataApiResult = await serviceClient.getDataCollectionMetadata(inventoryIdsForSurvey);

        if (!metadataApiResult.success || !metadataApiResult.data) {
            dispatch({
                type: SurveySetUIState,
                value: {
                    showErrorMessage: true
                }
            });
            return;
        }

        const fromVerifiedAddresses = metadataApiResult.data.surveyData.verifiedEmailUsers.filter(v => v.emailAddress == state.appServices!.userContext!.emailAddress);
        const fromConnectedAddresses = metadataApiResult.data.surveyData.connectedEmailUsers.filter(v => v.emailAddress == state.appServices!.userContext!.emailAddress);
        dispatch({
            type: SurveySetSurveyMetadata,
            value: {
                ...metadataApiResult.data.surveyData
            }
        });
        dispatch({
            type: SurveySetSurveyRequest,
            value: {
                bomType: 0,
                nativeLanguage: state.appServices?.getUserCultureNameOrDefault(),
                languageList: [state.appServices?.getUserCultureNameOrDefault() ?? "en"],
                useManufacturerLogo: metadataApiResult.data.surveyData.manufacturerLogoFileId != null,
                languageToSendIn: state.appServices?.getUserCultureNameOrDefault(),
                fromEmailAddress: fromConnectedAddresses.length > 0 ? fromConnectedAddresses[0].sendingEmailAddress : (fromVerifiedAddresses.length > 0 ? fromVerifiedAddresses[0].emailAddress : (state.appServices?.userContext?.emailAddress ?? "")),
                fromName: fromConnectedAddresses.length > 0 ? fromConnectedAddresses[0].userName : (fromVerifiedAddresses.length > 0 ? fromVerifiedAddresses[0].userName : (state.appServices?.userContext?.fullName ?? ""))
            }
        });
        dispatch({
            type: SurveySetUIState,
            value: {
                currentState: forTemplates ? SurveyMode.Template : SurveyMode.New,
                currentLanguage: state.appServices?.getUserCultureNameOrDefault(),
                currentSection: DisplaySection.MaterialDisclosure,
                currentPage: forTemplates ? 1 : 0
            }
        });

        const templateApiResult = await serviceClient.getDataCollectionTemplatesAll();

        let templates: SurveyTemplateDataModel[] = [];

        if (templateApiResult.result === ResultType.Ok) {
            templates = templateApiResult.data ?? [];
            dispatch({
                type: SurveySetSurveyMetadata,
                value: {
                    templates: templates
                }
            });
        }
    },

    // ===============================================================================

    setSurveyUIData: (value: Partial<SurveyUi>) => ({
        type: SurveySetUIState,
        value: value
    }),

    // ===============================================================================

    setFromEmailAddress: (emailAddress: string): AppThunkAction<KnownAction> => (disptach, getState) => {
        const state = getState().createAndEditSurveysState;
        if (!state) {
            return;
        }

        const verifiedUser = state.surveyMetadata.verifiedEmailUsers.firstOrDefault(v => v.sendingEmailAddress === emailAddress);
        const connectedUser = state.surveyMetadata.connectedEmailUsers.firstOrDefault(v => v.sendingEmailAddress === emailAddress);

        let value: Partial<SurveyRequest> = {}

        if (connectedUser !== null) {
            value = {
                fromEmailAddress: connectedUser.sendingEmailAddress,
                fromName: connectedUser.userName
            }
        } else if (verifiedUser !== null) {
            value = {
                fromEmailAddress: verifiedUser.emailAddress,
                fromName: verifiedUser.userName
            }
        } else {
            value = {
                fromEmailAddress: emailAddress
            }
        }

        disptach({
            type: SurveySetSurveyRequest,
            value: value
        });
    },

    // ===============================================================================
    setSurveyRequestData: (value: Partial<SurveyRequest>): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = getState().createAndEditSurveysState;
        if (!state) {
            return;
        }

        dispatch({
            type: SurveySetSurveyRequest,
            value: value
        });
        saveData(dispatch, getState);
    },

    // ===============================================================================
    setSurveyMetadata: (value: Partial<SurveyData>): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = getState().createAndEditSurveysState;

        if (!state || !value) {
            return;
        }

        dispatch({
            type: SurveySetSurveyMetadata,
            value: value
        });
    },

    // ===============================================================================
    setDataRequested: (value: Partial<SurveyDataRequested>): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = getState().createAndEditSurveysState;
        if (!state) {
            return;
        }
        dispatch({
            type: SurveySetSurveyRequest,
            value: {
                dataRequested: {
                    ...state.surveyRequest.dataRequested,
                    ...value,
                }
            }
        });
        saveData(dispatch, getState);
    },

    // ============================================================================

    saveNewSurveyAsDraft: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        saveSurvey(getState, null, dispatch);
    },

    // ===========================================================================

    // null for flags means use default flags for the given column
    updateRequestedBomColumnRequirementState: (columnId: string, flags: ColumnRequiredFlags | null): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = getState().createAndEditSurveysState;
        if (!state) {
            return;
        }

        const columnIndexToUpdate = state.surveyRequest.fields.findIndex(columnData => columnData.columnId === columnId);

        if (columnIndexToUpdate === -1) {
            return;
        }

        if (flags === null) {
            let defaultFlags = columnIdToDefaultColumnRequiredFlagsTable[columnId];
            if (!defaultFlags) {
                defaultFlags = { requiredInSummaryRow: false, requiredInNonSummaryNonLeafRows: true, requiredInLeafRows: true };
            }

            flags = defaultFlags;
        }

        const newColumnData = {
            ...state.surveyRequest.fields[columnIndexToUpdate],
            ...flags
        };

        const newFields = [...state.surveyRequest.fields];

        newFields[columnIndexToUpdate] = newColumnData;

        dispatch({
            type: SurveySetSurveyRequest,
            value: {
                fields: newFields
            }
        });
    },

    // ===========================================================================

    toggleBomColumn: (column: SurveyableColumn, columnExistsInSurveyRequest: boolean, customRequiredFlags?: ColumnRequiredFlags): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const state = getState().createAndEditSurveysState;
        if (!state) {
            return;
        }

        const columnIds = [column.columnId];

        if (column.pairedColumnId !== null) {
            columnIds.push(column.pairedColumnId);
        }

        // Custom (extensible-column-N) columns carry their stable snapshot name so
        // downstream save/send payloads remain safe if the BOM template is reordered.
        const columnNameByColumnId: { [columnId: string]: string | undefined } = {};
        if (column.isCustomColumn && column.columnName) {
            columnNameByColumnId[column.columnId] = column.columnName;
        }

        if (columnExistsInSurveyRequest) {
            // remove columnIds
            dispatch({
                type: SurveySetSurveyRequest,
                value: {
                    fields: state.surveyRequest.fields.filter(bomColumn => !columnIds.some(v => v === bomColumn.columnId))
                }
            })
        } else {
            // add columnIds
            const existingColumnIds = new Set(state.surveyRequest.fields.map(field => field.columnId));
            const columnIdsToAdd = columnIds.filter(columnId => !existingColumnIds.has(columnId));

            if (columnIdsToAdd.length === 0) {
                return;
            }

            dispatch({
                type: SurveySetSurveyRequest,
                value: {
                    fields: state.surveyRequest.fields.concat(columnIdsToAdd.map<SurveyRequestedBomColumn>(columnId => {
                        let requiredFlags = customRequiredFlags ?? columnIdToDefaultColumnRequiredFlagsTable[columnId];

                        if (!requiredFlags) {
                            requiredFlags = { requiredInSummaryRow: false, requiredInNonSummaryNonLeafRows: true, requiredInLeafRows: true };
                        }

                        return (
                            {
                                columnId: columnId,
                                columnName: columnNameByColumnId[columnId] ?? null,
                                requiredInLeafRows: requiredFlags.requiredInLeafRows,
                                requiredInNonSummaryNonLeafRows: requiredFlags.requiredInNonSummaryNonLeafRows,
                                requiredInSummaryRow: requiredFlags.requiredInSummaryRow
                            }
                        );
                    }))
                }
            })
        }
    },

    // ===========================================================================

    addOfflineResponse: (): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const state = getState().createAndEditSurveysState;
        if (!state) {
            return;
        }
        await sendOfflineResponse(state.surveyRequest.requestId, state.surveyUi.offlineResponseSupplierResponseInputText,
            state.surveyUi.offlineResponseNotesInputText, state.surveyUi.offlineResponseAttachments, dispatch, state);
    },

    // ===========================================================================

    saveExistingDraftSurveyManually: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const requestId = getState().createAndEditSurveysState?.surveyRequest.requestId;
        if (requestId != null) {
            saveSurvey(getState, requestId, dispatch);
        }
    },

    // ===========================================================================

    fetchInventorySummaryMetadata: (supplierRowModels: InviteSupplierModel[]): AppThunkActionAsync<KnownAction> => async (dispatch, getState) => {
        const appState = getState();

        const state = appState.createAndEditSurveysState;

        if (!state) {
            return;
        }

        const inventoryIdsToFetchFor = supplierRowModels
            .selectMany(rowModel => rowModel.requestedMaterials.map(r => r.inventoryId))
            .filter(inventoryId => inventoryId !== null)
            .map(inventoryId => inventoryId!);

        const serviceClient = new ToxnotServiceClient();

        const apiResult = await serviceClient.dataCollectionRequestGetInventoryMetadata(inventoryIdsToFetchFor);

        if (!apiResult.success || apiResult.data === null) {
            appState.appServices?.displayApiErrorMessage();
            return;
        }

        const newInventorySummaryMetadata = {
            ...state.surveyMetadata.inventoryIdToSummaryRowContentsMap,
            ...apiResult.data
        };

        dispatch({ type: SurveySetSurveyMetadata, value: { inventoryIdToSummaryRowContentsMap: newInventorySummaryMetadata } });
    }
}

// ===============================================================================

export const reducer: Reducer<SurveyState> = (state: SurveyState | undefined, action: Action): SurveyState => {
    if (state === undefined) {
        return getInitialState("en", true);
    }

    switch (action.type) {
        case SurveySetUIState:
            const surveyState = (action as SurveySetUIState).value;
            return {
                ...state,
                surveyUi: {
                    ...state.surveyUi,
                    ...surveyState
                }
            }
        case SurveySetSurveyMetadata:
            return {
                ...state,
                surveyMetadata: {
                    ...state.surveyMetadata,
                    ...(action as SurveySetSurveyMetadata).value
                }
            }
        case SurveySetSurveyRequest:
            const newState = {
                ...state,
                surveyRequest: {
                    ...state.surveyRequest,
                    ...(action as SurveySetSurveyRequest).value
                },
                lastSeenReduxAction: SurveySetSurveyRequest
            };

            const validNotRedundant = validateSurveyForRedundantRequests(newState.surveyRequest, newState.surveyMetadata);

            newState.surveyUi.surveyHasRequestsThatAreRedundantWithCustomQuestionnaire = !validNotRedundant;

            return newState;
    }
    return state;
}

// ===============Data Sync==============================================

function saveData(dispatch: (action: KnownAction) => void, getState: () => ApplicationState) {
    const appState = getState();

    const newState = appState.createAndEditSurveysState;
    if (!newState) {
        return;
    }
    if (newState && newState.surveyUi.currentState === SurveyMode.EditExisting && newState.surveyUi.dataLoaded) {
        //Transpose data from select materials table into responseHistoryMetadata
        saveSurvey(getState, newState.surveyRequest.requestId, dispatch);
    }
    // Auto-saving disabled - templates now save manually via save button
    // if (newState && newState.surveyUi.currentState === SurveyMode.Template && newState.surveyUi.dataLoaded && newState.surveyUi.currentTemplateId) {
    //     saveSurveyTemplate(dispatch, newState, newState.surveyUi.currentTemplateId, appState.appServices!);
    // }
}

//================Helper Methods=========================================

/**
 * Checks to see if the survey is requesting data (e.g. compliance) that is redundant
 * with data requests inside the custom questionnaire.
 * 
 * returns true iff no redundancy
 */
function validateSurveyForRedundantRequests(surveyRequest: SurveyRequest, surveyMetadata: SurveyData) {
    const complianceValueToFriendlyNameMap = Models.SurveyComplianceRegulationValues.getLocalizedFriendlyDescriptionMap(surveyMetadata.dynamicRegulations);

    if (!surveyRequest.dataRequested.customQuestionsAsked ||
        (surveyRequest.bomQuestionnaireId === null)) {
        return true;
    }

    if (!surveyRequest.dataRequested.complianceDataAsked) {
        return true;
    }

    const bomQuestionnaire = surveyMetadata.questionnaireTable[surveyRequest.bomQuestionnaireId ?? ""];

    if (!bomQuestionnaire) {
        return true;
    }

    const complianceRegulationToQuestionnairePageTypeMap: { [regulation: string]: QuestionnairePageType } = {
        "California Proposition 65": QuestionnairePageType.CaProp65,
        "Conflict Minerals (Legacy)": QuestionnairePageType.ConflictMinerals,
        "Conflict Minerals (CMRT)": QuestionnairePageType.ConflictMineralsV2,
        "REACH - Candidate List": QuestionnairePageType.REACHCompliance,
        "EU REACH SCIP Database": QuestionnairePageType.ScipQuestionnaire,
        "REACH - Authorisation List": QuestionnairePageType.ReachAuthorisationCompliance,
        "RoHS": QuestionnairePageType.RoHSCompliance
    };

    for (let questionnairePage of bomQuestionnaire.pages) {
        for (let complianceRegulationInSurvey of surveyRequest.dataRequested.complianceList) {
            const matchingPageTitle = complianceValueToFriendlyNameMap[complianceRegulationInSurvey];
            const matchingPageType = complianceRegulationToQuestionnairePageTypeMap[matchingPageTitle];

            if (questionnairePage.type === matchingPageType) {
                return false;
            }
        }
    }

    return true;
}

// ===============================================================================

async function saveSurvey(getState: () => ApplicationState, requestId: string | null, dispatch: (action: KnownAction) => void) {

    const state = getState().createAndEditSurveysState!;
    const selectMaterialsAndSuppliersState = getState().selectSuppliersMaterialsTableState!;

    function dispatchFailedResult() {
        dispatch({
            type: SurveySetUIState,
            value: {
                showErrorMessage: true,
                showDataSaveMessage: false
            }
        });
    }

    let apiResult;

    if (requestId != null) {

        // Save data from a draft survey 
        apiResult = await saveExistingSurvey(state.surveyRequest, state.surveyMetadata, selectMaterialsAndSuppliersState.supplierRowModels);

        if (!apiResult.success || apiResult.result !== ResultType.Ok) {
            dispatchFailedResult();
            return;
        }

        dispatch({
            type: SurveySetUIState,
            value: {
                showDataSaveMessage: true,
                showErrorMessage: false
            }
        });

    } else {

        // Save a New survey as a draft survey

        //Check for Empty Supplier
        if (selectMaterialsAndSuppliersState.supplierRowModels.length === 0 || selectMaterialsAndSuppliersState.supplierRowModels.some(v => v.contactEmail.trim().length === 0 || v.supplierName.trim().length === 0)) {
            alert(getState().appServices?.localizeString("The company name and email address fields cannot be blank."));
            return;
        }

        $("#offline-survey-progress-modal").modal("show");

        apiResult = await sendOrSaveNewSurvey(state.surveyMetadata, state.surveyRequest, state.surveyUi, selectMaterialsAndSuppliersState.supplierRowModels, getState().appServices!, dispatch, false);

        if (!apiResult.success || apiResult.result !== ResultType.Ok || apiResult.data === null) {
            dispatchFailedResult();
            return;
        }

        dispatch({
            type: SurveySetUIState,
            value: {
                showDataSaveMessage: true,
                showErrorMessage: false,
                activeSendOfflineProcessId: apiResult.data
            }
        });
    }

    if (state.surveyUi.currentState === SurveyMode.New) window.location.href = "/Surveys/SupplierSurveys";

}

// ===============================================================================

async function saveExistingSurvey(surveyRequest: SurveyRequest, surveyMetadata: SurveyData, selectedSuppliersMaterials: InviteSupplierModel[]) {

    const serviceClient = new ToxnotServiceClient();

    // NOTE: selectedSuppliersMaterials comes from a Redux slice that is reset to empty
    // whenever SelectSuppliersMaterialsTable unmounts (e.g. its own state-owning
    // component crashed and was contained by an error boundary elsewhere in the
    // survey editor). An empty array here does not necessarily mean the user cleared
    // their materials, so we only rebuild requestInventoryMetadataList when we actually
    // have supplier/material selection data to build it from; otherwise we preserve
    // whatever the survey already had saved.
    const requestInventoryMetadataList =
        selectedSuppliersMaterials.length === 0
            ? surveyRequest.requestInventoryMetadataList
            : selectedSuppliersMaterials[0].requestedMaterials.map((v): SurveyRequestInventoryMetadata => ({ inventoryId: v.inventoryId!, customizedName: v.customName ?? "", originalName: v.name }));

    const surveyRequestNew: SurveyRequest = {
        ...surveyRequest,
        sdsRequested: surveyRequest.dataRequested.materialDisclosureAsked && surveyRequest.sdsRequested,
        requestInventoryMetadataList,
        dataRequested: {
            ...surveyRequest.dataRequested,
            documentsCertificationList: surveyRequest.dataRequested.documentsAsked ? surveyRequest.dataRequested.documentsCertificationList : [],
            complianceList: surveyRequest.dataRequested.complianceDataAsked ? surveyRequest.dataRequested.complianceList : []
        },
        supplierQuestionnaireId: surveyRequest.dataRequested.customQuestionsAsked ? surveyRequest.supplierQuestionnaireId : null,
        bomQuestionnaireId: surveyRequest.dataRequested.customQuestionsAsked ? surveyRequest.bomQuestionnaireId : null,
        attestationQuestionnaireId: surveyRequest.dataRequested.customQuestionsAsked ? surveyRequest.attestationQuestionnaireId : null,
        preFillBomQuestionnaireFromSummaryData: surveyRequest.preFillBomQuestionnaireFromSummaryData,
        preFillSupplierQuestionnaireFromSummaryData: surveyRequest.preFillSupplierQuestionnaireFromSummaryData,
        makeBomDataRequiredOnQuestionnaireAnswer: surveyRequest.makeBomDataRequiredOnQuestionnaireAnswer,
        questionnaireAnswerThatMakesBomDataRequired: surveyRequest.questionnaireAnswerThatMakesBomDataRequired,
        includeAttestationLetter: surveyRequest.includeAttestationLetter
    };

    const apiResult = await serviceClient.surveyRequestUpdate(surveyRequestNew);

    return apiResult;
}

// ===============================================================================

async function sendOrSaveNewSurvey(surveyMetadata: SurveyData, surveyRequest: SurveyRequest, surveyUi: SurveyUi, selectedSuppliersMaterials: InviteSupplierModel[], appServices: AppServices, dispatch: (action: KnownAction) => void, shouldSend: boolean) {

    const data: SurveyEditorCreateNewSurveyRequestData = {
        ...surveyRequest,
        sdsRequested: surveyRequest.dataRequested.materialDisclosureAsked && surveyRequest.sdsRequested,
        supplierModels: selectedSuppliersMaterials,
        shouldSend: shouldSend,
        surveyTemplateId: surveyUi.currentTemplateId ?? surveyRequest.surveyTemplateId,
        ...surveyRequest.dataRequested,
        questionnaireAnswerThatMakesBomDataRequired: surveyRequest.dataRequested.customQuestionsAsked ? surveyRequest.questionnaireAnswerThatMakesBomDataRequired ?? null : null,
        documentsCertificationsAskedList: surveyRequest.dataRequested.documentsAsked ? surveyRequest.dataRequested.documentsCertificationList : [],
        complianceDataAskedList: surveyRequest.dataRequested.complianceDataAsked ? surveyRequest.dataRequested.complianceList : [],
        supplierQuestionnaireId: surveyRequest.dataRequested.customQuestionsAsked ? surveyRequest.supplierQuestionnaireId : null,
        bomQuestionnaireId: surveyRequest.dataRequested.customQuestionsAsked ? surveyRequest.bomQuestionnaireId : null,
        attestationQuestionnaireId: surveyRequest.dataRequested.customQuestionsAsked ? surveyRequest.attestationQuestionnaireId : null
    }

    if (shouldSend) {
        appServices.trackEvent("sent-surveys", data.supplierModels.length);
        if (data.languageList.length >= 2) {
            appServices.trackEvent("sent-survey-with-multiple-languages");
        }
    }

    const serviceClient = new ToxnotServiceClient();
    const apiResult = await serviceClient.createDataCollectionRequest(data);
    return apiResult;
}

// ===============================================================================
async function sendDraftSurvey(data: SurveyRequestApiRequestBundle, requestId: string | null, appServices: AppServices, dispatch: (action: KnownAction) => void) {
    const serviceClient = new ToxnotServiceClient();

    appServices.trackEvent("sent-surveys", 1);

    if (data.surveyRequest.languageList.length >= 2) {
        appServices.trackEvent("sent-survey-with-multiple-languages");
    }

    const updateResult = await serviceClient.surveyRequestUpdate(data.surveyRequest);

    if (!updateResult.success) {
        dispatch({
            type: SurveySetUIState,
            value: {
                surveySendStateType: SurveySendStateType.SendSurveyFailed
            }
        });

        return {
            ...updateResult,
            data: null
        };
    }

    const apiResult = await serviceClient.sendDataCollectionRequestDraft(requestId!);

    if (!apiResult.success) {
        dispatch({
            type: SurveySetUIState,
            value: {
                surveySendStateType: SurveySendStateType.SendSurveyFailed
            }
        });
    }

    return apiResult;
}

// ===============================================================================

async function getSurvey(requestId: string | null, dispatch: (action: KnownAction) => void) {
    const serviceClient = new ToxnotServiceClient();
    if (requestId === null) {
        return;
    }

    const apiResult = await serviceClient.getDataCollectionRequest(requestId, 300000);

    if (!apiResult.success || (apiResult.result !== ResultType.Ok) || !apiResult.data || !apiResult.data.surveyRequest) {
        throw new Error("Failed to get survey");
    }

    const surveyRequest = apiResult.data.surveyRequest;
    const surveyData = apiResult.data.surveyData;

    surveyData.offlineProcessQueuedMaterials = getOfflineProcessQueuedMaterials(surveyData.offlineProcessDataResponses);

    let revisionRequestManager: RevisionRequestManager | null = null;

    if (surveyRequest.responseHistoryList.length > 0) {
        revisionRequestManager = new RevisionRequestManager(surveyRequest.requestId!, surveyRequest.responseHistoryList[0].historyId);

        revisionRequestManager.loadSurveyHistoryEntryRevisionRequests();
    }

    dispatch({
        type: SurveySetUIState,
        value: {
            revisionRequestManager: revisionRequestManager
        }
    });

    dispatch({
        type: SurveySetSurveyRequest,
        value: {
            ...surveyRequest,
            dataRequested: {
                ...surveyRequest.dataRequested,
                complianceDataAsked: surveyRequest.dataRequested.complianceList.length > 0,
                documentsAsked: surveyRequest.dataRequested.documentsCertificationList.length > 0,
                customQuestionsAsked: surveyRequest.bomQuestionnaireId != null || surveyRequest.supplierQuestionnaireId != null || surveyRequest.attestationQuestionnaireId != null,
                sustainabilityDataAsked: surveyRequest.dataRequested.embodiedCarbonDataAsked || surveyRequest.dataRequested.waterUseDataAsked || surveyRequest.dataRequested.carbonOtherDataAsked || surveyRequest.dataRequested.carbonTransparencyDataAsked
            }
        }
    });

    dispatch({
        type: SurveySetSurveyMetadata,
        value: {
            ...surveyData
        }
    });

    let templates: SurveyTemplateDataModel[] = [];

    const templateApiResult = await serviceClient.getDataCollectionTemplatesAll();

    if (templateApiResult.result === ResultType.Ok) {
        templates = templateApiResult.data ?? [];

        dispatch({
            type: SurveySetSurveyMetadata,
            value: {
                templates: templates
            }
        });
    }

    dispatch({
        type: SurveySetUIState,
        value: {
            currentPage: (surveyRequest.state === 4) ? 0 : 4,
            revisionRequestManager: revisionRequestManager,
            currentSection: (surveyRequest.bomType === 2) ? DisplaySection.CustomQuestions : DisplaySection.MaterialDisclosure,
            currentState: SurveyMode.EditExisting
        }
    });
}

// ===============================================================================

function getOfflineProcessQueuedMaterials(dataResponses: any[]) {
    const offlineProcessQueuedMaterials: any[] = [];

    for (var i = 0; i < dataResponses.length; i++) {
        if (dataResponses[i].state.processingStage !== Models.OfflineProcessingStageType.TotallyComplete.toString() &&
            dataResponses[i].state.processingStage !== Models.OfflineProcessingStageType.Failed.toString()) {
            let inventoryIds = dataResponses[i].context.requestorResponseInventoryIds;

            for (var j = 0; j < inventoryIds.length; j++) {
                let id = inventoryIds[j];

                if (offlineProcessQueuedMaterials.indexOf(id) === -1) {
                    offlineProcessQueuedMaterials.push(id);
                }
            }
        }
    }

    return offlineProcessQueuedMaterials;
}

// ===============================================================================

export function doLinkedMaterialsExist(surveyState: SurveyState) {
    return Object.values(surveyState.surveyMetadata.inventoryIdToSummaryRowContentsMap).some(s => s.hasLinkedMaterials);
}

// ===============================================================================

async function deleteSurveyTemplate(templateId: string | null, dispatch: (action: KnownAction) => void, templates: SurveyTemplateDataModel[]) {
    const serviceClient = new ToxnotServiceClient();
    if (templateId != null) {
        const apiResult = await serviceClient.deleteDataCollectionTemplate(templateId);
        if (apiResult.success && apiResult.result === ResultType.Ok) {
            dispatch({
                type: SurveySetSurveyMetadata,
                value: {
                    templates: templates.filter((v, i) => v.templateId != templateId)
                }
            });
        }
    }
}

// ===============================================================================
// Updates both as a new or an existing template
async function saveSurveyTemplate(dispatch: (action: KnownAction) => void, state: SurveyState, existingTemplateId: string | null, appServices: AppServices) {
    const serviceClient = new ToxnotServiceClient();
    const existingTemplate = state.surveyMetadata.templates.find(v => v.templateId === existingTemplateId);
    const existingTemplateName = existingTemplate?.name ?? "";
    const model: SurveyTemplateDataModel = {
        ownerId: existingTemplate?.ownerId ?? "",
        templateId: existingTemplateId,
        name: existingTemplateId ? existingTemplateName : state.surveyUi.newTemplateName,
        dataRequested: {
            ...state.surveyRequest.dataRequested,
            embodiedCarbonDataAsked: state.surveyRequest.dataRequested.sustainabilityDataAsked ? state.surveyRequest.dataRequested.embodiedCarbonDataAsked : false,
            waterUseDataAsked: state.surveyRequest.dataRequested.sustainabilityDataAsked ? state.surveyRequest.dataRequested.waterUseDataAsked : false,
            carbonTransparencyDataAsked: state.surveyRequest.dataRequested.sustainabilityDataAsked ? state.surveyRequest.dataRequested.carbonTransparencyDataAsked : false,
            carbonOtherDataAsked: state.surveyRequest.dataRequested.sustainabilityDataAsked ? state.surveyRequest.dataRequested.carbonOtherDataAsked : false,
            vocOtherDataAsked: state.surveyRequest.dataRequested.vocDataAsked ? state.surveyRequest.dataRequested.vocOtherDataAsked : false,
            vocTransparencyDataAsked: state.surveyRequest.dataRequested.vocDataAsked ? state.surveyRequest.dataRequested.vocTransparencyDataAsked : false,
            documentsCertificationList: state.surveyRequest.dataRequested.documentsAsked ? state.surveyRequest.dataRequested.documentsCertificationList : [],
            complianceList: state.surveyRequest.dataRequested.complianceDataAsked ? state.surveyRequest.dataRequested.complianceList : []
        },
        bomQuestionnaireId: state.surveyRequest.dataRequested.customQuestionsAsked ? state.surveyRequest.bomQuestionnaireId : null,
        supplierQuestionnaireId: state.surveyRequest.dataRequested.customQuestionsAsked ? state.surveyRequest.supplierQuestionnaireId : null,
        attestationQuestionnaireId: state.surveyRequest.dataRequested.customQuestionsAsked ? state.surveyRequest.attestationQuestionnaireId : null,
        languageList: state.surveyRequest.languageList,
        nativeLanguage: state.surveyRequest.nativeLanguage,
        ccEmailList: state.surveyRequest.ccEmailList.join(", "),
        emailSubject: state.surveyRequest.emailSubject,
        emailMessage: state.surveyRequest.emailMessage,
        requestInstructions: state.surveyRequest.requestInstructions,
        completionInstructions: state.surveyRequest.completionInstructions,
        languageToEmailSubjectMap: state.surveyRequest.languageToEmailSubjectMap,
        languageToEmailMessageMap: state.surveyRequest.languageToEmailMessageMap,
        languageToRequestInstructionsMap: state.surveyRequest.languageToRequestInstructionsMap,
        languageToCompletionInstructionsMap: state.surveyRequest.languageToCompletionInstructionsMap,
        scheduledReminders: state.surveyRequest.scheduledReminders,
        sdsRequested: state.surveyRequest.dataRequested.materialDisclosureAsked && state.surveyRequest.sdsRequested,
        questionnaireAnswerThatMakesBomDataRequired: state.surveyRequest.dataRequested.customQuestionsAsked && state.surveyRequest.makeBomDataRequiredOnQuestionnaireAnswer ? state.surveyRequest.questionnaireAnswerThatMakesBomDataRequired : null,
        bomColumns: preserveBomColumnNames(state.surveyRequest.fields),
        useManufacturerLogo: state.surveyRequest.useManufacturerLogo,
        preFillBomQuestionnaireFromSummaryData: state.surveyRequest.dataRequested.customQuestionsAsked ? state.surveyRequest.preFillBomQuestionnaireFromSummaryData : false,
        preFillSupplierQuestionnaireFromSummaryData: state.surveyRequest.dataRequested.customQuestionsAsked ? state.surveyRequest.preFillSupplierQuestionnaireFromSummaryData : false,
        makeBomDataRequiredOnQuestionnaireAnswer: state.surveyRequest.dataRequested.customQuestionsAsked ? state.surveyRequest.makeBomDataRequiredOnQuestionnaireAnswer : false,
        requireSuppliersToDiscloseRegulatoryIngredients: state.surveyRequest.metadata.requireSuppliersToDiscloseRegulatoryIngredients,
        additionalColumnIdIdentifiers: state.surveyRequest.metadata.additionalColumnIdIdentifiers,
        includeAttestationLetter: state.surveyRequest.includeAttestationLetter ? state.surveyRequest.includeAttestationLetter : false,
        lastModified: null,
        lastModifiedBy: null,
        lastModifiedByEmail: null
    };

    //  All templates must have a valid name.
    if (model.name.trim() === "") {
        model.name = appServices.localizeString("Unknown Survey Template.");
    }

    const apiResult = await serviceClient.updateDataCollectionTemplate(model);
    if (apiResult.success && apiResult.result === ResultType.Ok) {
        // EXG-7722 (M2): Surface any user-facing warning returned by the server (e.g.
        // custom BOM columns that were dropped because they are no longer available in
        // the account BOM template). Product requires users to be told when a deleted
        // custom column is removed from a survey template.
        if (apiResult.resultMessage) {
            alert(apiResult.resultMessage);
        }

        model.templateId = apiResult.data;
        
        // Update the current template ID in UI state to preserve it for subsequent API calls
        dispatch({
            type: SurveySetUIState,
            value: {
                currentTemplateId: apiResult.data
            }
        });
        
        if (existingTemplateId) {
            dispatch({
                type: SurveySetSurveyMetadata,
                value: {
                    templates: [
                        ...state.surveyMetadata.templates.map((value, i) => {
                            if (value.templateId === existingTemplateId) {
                                value = model
                            }
                            return value
                        })
                    ]
                }
            });
            return;
        }

        dispatch({
            type: SurveySetSurveyMetadata,
            value: {
                templates: state.surveyMetadata.templates.concat(model)
            }
        });//Save Template
    }
}

// ===============================================================================

async function sendReminder(requestId: string | null, message: string, dispatch: (action: KnownAction) => void) {
    if (requestId === null) {
        return;
    }
    const serviceClient = new ToxnotServiceClient();

    const apiResult = await serviceClient.sendRequestReminderEmails([requestId], message);
    if (apiResult.result === ResultType.Ok) {
        dispatch({
            type: SurveySetUIState,
            value: { sentReminderAlert: true, errorReminderAlert: false, errorReminderNoMessageAlert: false }
        })
    } else {
        dispatch({
            type: SurveySetUIState,
            value: { errorReminderAlert: true, sentReminderAlert: false, errorReminderNoMessageAlert: false }
        })
    }
}

// ===============================================================================

async function surveySetAsNoValidationRequired(requestId: string | null, dispatch: (action: KnownAction) => void) {
    const serviceClient = new ToxnotServiceClient();
    if (requestId != null) {
        const apiResult = await serviceClient.surveySetAsNoValidationRequired(requestId);
        if (apiResult.result == ResultType.Ok && apiResult.success) {
            dispatch({
                type: SurveySetSurveyRequest,
                value: {
                    requireValidationOnResponse: false
                }
            })
        }
    }
}

// ============================================================================

async function sendOfflineResponse(requestId: string | null, responseText: string, notes: string, fileList: FileList | null, dispatch: (action: KnownAction) => void, state: SurveyState) {
    const serviceClient = new ToxnotServiceClient();
    if (requestId === null) {
        return
    }
    const uploadedFileIds: string[] = [];
    const uploadedFiles: [fileId: string, fileName: string][] = [];
    if (fileList != null) {
        for (let x = 0; x < fileList.length; x++) {
            const file = fileList.item(x);
            if (file != null) {
                const apiResult = await serviceClient.uploadGeneralFile(file.name, "", 0, false, false, file);
                if (apiResult.data != null && apiResult.result === ResultType.Ok) {
                    uploadedFileIds.push(apiResult.data.fileId);
                    uploadedFiles.push([apiResult.data.fileId, file.name]);
                }
            }
        }
    }

    const dataToUpload: OfflineResponseApiData = {
        requestId: requestId,
        supplierResponseText: responseText,
        notes: notes,
        uploadedFileIds: uploadedFileIds
    }

    const offlineResponseApiResult = await serviceClient.surveyRequestAddOfflineResponse(dataToUpload);
    let success = false;
    if (offlineResponseApiResult.result === ResultType.Ok) {
        success = true;
        if (offlineResponseApiResult.data != null) {
            const newHistoryEntry = offlineResponseApiResult.data;
            dispatch({
                type: SurveySetSurveyRequest,
                value: {
                    responseHistoryList: state.surveyRequest.responseHistoryList.concat(newHistoryEntry)
                }
            });
            for (let f of uploadedFiles) {
                dispatch({
                    type: SurveySetSurveyMetadata,
                    value: {
                        offlineResponseFileTable: {
                            ...state.surveyMetadata.offlineResponseFileTable,
                            [f[0]]: { filename: f[1] }
                        }
                    }
                });
            }
        }
    }
    dispatch({
        type: SurveySetUIState,
        value: {
            addOfflineResponseSuccess: success
        }
    });
}
